import { ClassData, ClassTuition, MeetingDays } from "../../../common/dtos/classes.type";
import { convertTo12HourFormat } from "../../../common/utils/date.util";
import { openModal } from "../../../common/views/modal.view";
import { groupEventsByMonth, isMultipleDays, parseDate } from "../utils";

const DAY_LABELS: Array<{ key: keyof MeetingDays; label: string }> = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function countMeetingDays(meetingDays: MeetingDays): number {
  return Object.values(meetingDays).filter(Boolean).length;
}

function isMonthlyBillingCycle(billingCycle?: string): boolean {
  return billingCycle?.trim().toLowerCase() === "monthly";
}

function hasExplicitMultiDayPricing(tuition: ClassTuition): boolean {
  return Object.values(tuition.days).some((v) => v > 0);
}

function parseDateParts(dateString: string) {
  const [year, month, day] = dateString.split("-").map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(Date.UTC(year, month - 1, day));
  return {
    date,
    year,
    month,
    day,
    monthLabel: MONTH_LABELS[month - 1] || "",
  };
}

function getDateSpanDays(startDate: string, endDate: string): number {
  const start = parseDateParts(startDate);
  const end = parseDateParts(endDate);
  if (!start || !end) return 0;

  return Math.floor((end.date.getTime() - start.date.getTime()) / 86400000) + 1;
}

function isLongRunningEvent(item: ClassData): boolean {
  return getDateSpanDays(item.start_date, item.end_date) > 14;
}

function formatMeetingDays(meetingDays: MeetingDays): string {
  const selected = DAY_LABELS
    .map((day, index) => ({ ...day, index }))
    .filter((day) => meetingDays[day.key]);

  if (!selected.length) return "";

  const isContiguous = selected.every(
    (day, index) => index === 0 || day.index === selected[index - 1].index + 1
  );

  if (selected.length > 1 && isContiguous) {
    return `${selected[0].label}-${selected[selected.length - 1].label}`;
  }

  return selected.map((day) => day.label).join(", ");
}

function formatDateRange(startDate: string, endDate: string): {
  dateRange: string;
  yearRange: string;
} {
  const start = parseDateParts(startDate);
  const end = parseDateParts(endDate);

  if (!start || !end) {
    return { dateRange: startDate, yearRange: "" };
  }

  if (startDate === endDate) {
    return {
      dateRange: `${start.monthLabel} ${start.day}`,
      yearRange: String(start.year),
    };
  }

  const dateRange =
    start.year === end.year && start.month === end.month
      ? `${start.monthLabel} ${start.day}-${end.day}`
      : `${start.monthLabel} ${start.day} - ${end.monthLabel} ${end.day}`;

  return {
    dateRange,
    yearRange:
      start.year === end.year ? String(start.year) : `${start.year}-${end.year}`,
  };
}

function getPriceSuffix(item: ClassData): string {
  if (isMultipleDays(item.meeting_days) && hasExplicitMultiDayPricing(item.tuition)) {
    return "/day";
  }

  if (isMonthlyBillingCycle(item.BillingCycle) && isLongRunningEvent(item)) {
    return "/monthly";
  }

  return isMultipleDays(item.meeting_days) ? "/day" : "";
}

function buildPriceBreakdownHtml(
  tuition: ClassTuition,
  meetingDays: MeetingDays,
  billingCycle?: string
): string {
  const totalDays = countMeetingDays(meetingDays);
  const hasMultiDayPricing = hasExplicitMultiDayPricing(tuition);
  if (totalDays <= 1 || (isMonthlyBillingCycle(billingCycle) && !hasMultiDayPricing)) return "";

  const cells: string[] = [];
  for (let d = 1; d <= totalDays; d++) {
    let price: number;
    if (d === 1) {
      price = tuition.fee;
    } else if (hasMultiDayPricing && tuition.days[`day_${d}` as keyof typeof tuition.days]) {
      price = tuition.days[`day_${d}` as keyof typeof tuition.days];
    } else {
      price = tuition.fee * d;
    }
    const isMax = d === totalDays;
    const label = d === 1 ? "1 day" : `${d} days`;
    cells.push(
      `<div class="bd-cell${isMax ? " highlight" : ""}">` +
        `<span class="bd-days">${label}</span>` +
        `<span class="bd-price">$${price}</span>` +
      `</div>`
    );
  }

  return `<div class="price-breakdown">${cells.join("")}</div>`;
}

export async function setEventView(
  events: ClassData[],
  htmlComponent: string,
  useDescription: boolean = false
): Promise<void> {
  const htmlSection = $(htmlComponent);
  htmlSection.empty();

  if (!events.length) {
    htmlSection.append(`<div class="no-content">
      <h3>No upcoming dates published</h3>
      <div><p>Check back in a few days or call <a href="tel:9102950724" target="_blank">910-295-0724</a> for more info</p></div>
      </div>`);
    return;
  }
  const eventsByMonth = groupEventsByMonth(events);
  eventsByMonth.forEach((monthGroup) => {
    htmlSection.append(`<h4>${monthGroup.title}</h4>`);

    monthGroup.items.forEach((item: any) => {
      const categoryLocation = item?.location;
      const priceSuffix = getPriceSuffix(item);
      const isLongRunning = isLongRunningEvent(item);

      const date = parseDate(item.start_date);
      const startTime = convertTo12HourFormat(item.start_time);
      const endTime = convertTo12HourFormat(item.end_time);
      const startMonth = parseDateParts(item.start_date)?.monthLabel ?? "";
      let eventCardTemplate = "";
      if (isMultipleDays(item.meeting_days)) {
        const endDate = parseDate(item.end_date);
        const endMonth = parseDateParts(item.end_date)?.monthLabel ?? "";
        const monthLabel =
          !startMonth || startMonth === endMonth
            ? startMonth
            : `${startMonth} – ${endMonth}`;
        const dateRange = formatDateRange(item.start_date, item.end_date);
        const recurringDateHtml = isLongRunning
          ? `
              <div class="multi-date recurring-date">
                <div class="weekdays">${formatMeetingDays(item.meeting_days)}</div>
                <div class="date-range">${dateRange.dateRange}</div>
                <div class="year-range">${dateRange.yearRange}</div>
              </div>
            `
          : `
              <div class="multi-date">
                ${monthLabel ? `<div class="month-label">${monthLabel}</div>` : ""}
                <div class="weekdays">
                  <span class="start">${date.weekday}</span>
                  <span class="and">-</span>
                  <span class="end">${endDate.weekday}</span>
                </div>
                <div class="day">
                  <span>${date.date}</span>
                  <span class="and">-</span>
                  <span>${endDate.date}</span>
                </div>
              </div>
            `;
        const priceBreakdownHtml = buildPriceBreakdownHtml(
          item.tuition,
          item.meeting_days,
          item.BillingCycle
        );
        eventCardTemplate = `
            <div class="event-card${priceBreakdownHtml ? " has-breakdown" : ""}">
              ${recurringDateHtml}
              <div class="name">
                  <h3>${item.name}</h3>
                  <h5><span class="start-time">${startTime.time} ${
          startTime.period
        }</span> - <span class="end-time">${endTime.time} ${
          endTime.period
        }</span></h5>
                  <p>${
                    useDescription ? item.description : item.location_name
                  }</p>
              </div>
              <div class="card-actions">
                  <div class="price">
                      <div>$<span class="text">${item.tuition.fee}</span>${priceSuffix ? `<span class="per-day">${priceSuffix}</span>` : ""}</div>
                  </div>
                  ${
                    item.openings.calculated_openings > 0
                      ? `
                    <button class="button enroll">
                      Enroll
                    </button>`
                      : `
                    <button class="button waitlist">Join Waitlist</button>
                    `
                  }
              </div>
              ${priceBreakdownHtml}
            </div>
          `;
      } else {
        eventCardTemplate = `
        <div class="event-card">
          <div class="date">
            ${startMonth ? `<span class="month-label">${startMonth}</span>` : ""}
            <span>
              ${date.weekday}
            </span>
            <span>
              ${date.date}
            </span>
          </div>
          <div class="name">
              <h3>${item.name}</h3>
              <h5><span class="start-time">${startTime.time} ${
          startTime.period
        }</span> - <span class="end-time">${endTime.time} ${
          endTime.period
        }</span></h5>
              <p>${item.location_name}</p>
          </div>
          <div>
              <div class="price">
                  <div>$<span class="text">${item.tuition.fee}</span>${priceSuffix ? `<span class="per-day">${priceSuffix}</span>` : ""}</div>
              </div>
              ${
                item.openings.calculated_openings > 0
                  ? `<button class="button enroll">Enroll</button>`
                  : `<button class="button waitlist">Join Waitlist</button>`
              }
          </div>
        </div>
      `;
      }
      const $eventCard = $(eventCardTemplate);

      // Pre-compute the modal display strings up here so the modal stays
      // dumb: it just reads the `__*` fields off cardData and renders them.
      const modalContext = {
        __priceSuffix: getPriceSuffix(item),
        __priceBreakdownHtml: buildPriceBreakdownHtml(
          item.tuition,
          item.meeting_days,
          item.BillingCycle
        ),
        __dateRangeLabel: isMultipleDays(item.meeting_days)
          ? formatDateRange(item.start_date, item.end_date).dateRange
          : "",
        __yearRangeLabel: isMultipleDays(item.meeting_days)
          ? formatDateRange(item.start_date, item.end_date).yearRange
          : "",
      };

      // Add event listener to the button
      $eventCard.find("button.enroll").on("click", (event) => {
        event.preventDefault();

        openModal({
          ...item,
          ...modalContext,
          location: categoryLocation,
          category2: item.name,
        } as any);
      });

      $eventCard.find("button.waitlist").on("click", (event) => {
        event.preventDefault();

        openModal({
          ...item,
          ...modalContext,
          location: categoryLocation,
          category2: item.name,
        } as any);
      });

      htmlSection.append($eventCard);
    });
  });
}
